import React, { useState } from 'react';
import { BilingualText } from '../components/BilingualText';
import { ArrowLeft, Calendar as CalendarIcon, ChevronLeft, ChevronRight, MoreVertical, Clock, Truck, Package, CheckCircle2, AlertCircle } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
type KanbanStatus = 'Pending' | 'Confirmed' | 'Arrived' | 'Unloading' | 'Completed';
interface Shipment {
  id: string;
  supplier: string;
  plate: string;
  time: string;
  items: number;
  status: KanbanStatus;
}
const initialShipments: Shipment[] = [{
  id: 'ASN-98231',
  supplier: 'Global Logistics',
  plate: 'LKR 4920',
  time: '08:00 AM',
  items: 42,
  status: 'Arrived'
}, {
  id: 'ASN-98235',
  supplier: 'FastTrack Trading',
  plate: 'KSA 1122',
  time: '09:30 AM',
  items: 15,
  status: 'Pending'
}, {
  id: 'ASN-98240',
  supplier: 'Prime Goods Ltd',
  plate: 'DXB 8833',
  time: '10:15 AM',
  items: 88,
  status: 'Confirmed'
}, {
  id: 'ASN-98244',
  supplier: 'Al-Futtaim Group',
  plate: 'AUH 4455',
  time: '11:00 AM',
  items: 24,
  status: 'Unloading'
}, {
  id: 'ASN-98248',
  supplier: 'Desert Express',
  plate: 'KSA 9900',
  time: '01:30 PM',
  items: 56,
  status: 'Completed'
}, {
  id: 'ASN-98250',
  supplier: 'Zajil Express',
  plate: 'LKR 1122',
  time: '02:45 PM',
  items: 30,
  status: 'Confirmed'
}];
const columns: {
  status: KanbanStatus;
  label: string;
  color: string;
}[] = [{
  status: 'Pending',
  label: 'Pending Approval',
  color: 'bg-yellow-500'
}, {
  status: 'Confirmed',
  label: 'Confirmed',
  color: 'bg-blue-500'
}, {
  status: 'Arrived',
  label: 'Truck Arrived',
  color: 'bg-indigo-500'
}, {
  status: 'Unloading',
  label: 'Unloading',
  color: 'bg-orange-500'
}, {
  status: 'Completed',
  label: 'Completed',
  color: 'bg-green-500'
}];
export function ShipmentsKanban() {
  const [viewDate, setViewDate] = useState(new Date('2026-07-11'));
  const navigate = useNavigate();
  return <div className="min-h-screen bg-slate-50 flex flex-col" data-id="element-1052">
      <header className="bg-white px-4 py-6 border-b border-slate-200 flex items-center justify-between sticky top-0 z-10 lg:px-8" data-id="element-1053">
        <div className="flex items-center gap-4" data-id="element-1054">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 text-slate-600 hover:bg-slate-50 rounded-full transition-colors" data-id="element-1055">
            <ArrowLeft size={24} data-id="element-1056" />
          </button>
          <BilingualText en="Shipments Kanban" ar="لوحة الشحنات" className="text-xl font-bold" data-id="element-1057" />
        </div>

        <div className="flex items-center gap-2 bg-slate-100 p-1 rounded-lg" data-id="element-1058">
          <button className="p-1.5 hover:bg-white hover:shadow-sm rounded-md transition-all text-slate-600" data-id="element-1059">
            <ChevronLeft size={18} data-id="element-1060" />
          </button>
          <div className="px-3 flex items-center gap-2 text-sm font-bold text-slate-700" data-id="element-1061">
            <CalendarIcon size={16} data-id="element-1062" />
            <span data-id="element-1063">July 11, 2026</span>
          </div>
          <button className="p-1.5 hover:bg-white hover:shadow-sm rounded-md transition-all text-slate-600" data-id="element-1064">
            <ChevronRight size={18} data-id="element-1065" />
          </button>
        </div>

        <div className="hidden md:flex items-center gap-2" data-id="element-1066">
          <button className="btn-secondary py-2 px-4 text-sm" data-id="element-1067">
            <span data-id="element-1068">Filters</span>
          </button>
          <button className="btn-primary py-2 px-4 text-sm" data-id="element-1069">
            <span data-id="element-1070">New Shipment</span>
          </button>
        </div>
      </header>

      <main className="flex-1 overflow-x-auto p-4 md:p-6" data-id="element-1071">
        <div className="flex gap-6 h-full min-w-max pb-4" data-id="element-1072">
          {columns.map(column => <div key={column.status} className="w-80 flex flex-col gap-4" data-id="element-1073">
              <div className="flex items-center justify-between px-2" data-id="element-1074">
                <div className="flex items-center gap-2" data-id="element-1075">
                  <div className={`w-2 h-2 rounded-full ${column.color}`} data-id="element-1076" />
                  <span className="font-bold text-slate-700" data-id="element-1077">
                    {column.label}
                  </span>
                  <span className="bg-slate-200 text-slate-600 text-[10px] px-2 py-0.5 rounded-full font-bold" data-id="element-1078">
                    {initialShipments.filter(s => s.status === column.status).length}
                  </span>
                </div>
                <button className="text-slate-400 hover:text-slate-600" data-id="element-1079">
                  <MoreVertical size={16} data-id="element-1080" />
                </button>
              </div>

              <div className="flex-1 space-y-3" data-id="element-1081">
                {initialShipments.filter(s => s.status === column.status).map(shipment => <KanbanCard key={shipment.id} shipment={shipment} data-id="element-1082" />)}

                <button className="w-full py-3 border-2 border-dashed border-slate-200 rounded-xl text-slate-400 text-sm font-medium hover:border-slate-300 hover:text-slate-500 transition-all flex items-center justify-center gap-2" data-id="element-1083">
                  <Plus size={16} data-id="element-1084" />
                  <span data-id="element-1085">Add Shipment</span>
                </button>
              </div>
            </div>)}
        </div>
      </main>
    </div>;
}
function KanbanCard({
  shipment
}: {
  shipment: Shipment;
}) {
  return <div className="card p-4 hover:shadow-md hover:border-[#2E3192]/30 transition-all cursor-grab active:cursor-grabbing group" data-id="element-1086">
      <div className="flex justify-between items-start mb-3" data-id="element-1087">
        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-1088">
          {shipment.id}
        </span>
        <button className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-slate-600 transition-opacity" data-id="element-1089">
          <MoreVertical size={14} data-id="element-1090" />
        </button>
      </div>

      <div className="space-y-3" data-id="element-1091">
        <div className="font-bold text-slate-800 leading-tight" data-id="element-1092">
          {shipment.supplier}
        </div>

        <div className="grid grid-cols-2 gap-2" data-id="element-1093">
          <div className="flex items-center gap-1.5 text-slate-500" data-id="element-1094">
            <Clock size={12} data-id="element-1095" />
            <span className="text-[11px] font-medium" data-id="element-1096">{shipment.time}</span>
          </div>
          <div className="flex items-center gap-1.5 text-slate-500" data-id="element-1097">
            <Truck size={12} data-id="element-1098" />
            <span className="text-[11px] font-medium" data-id="element-1099">{shipment.plate}</span>
          </div>
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-slate-50" data-id="element-1100">
          <div className="flex items-center gap-1.5 text-slate-400" data-id="element-1101">
            <Package size={12} data-id="element-1102" />
            <span className="text-[11px] font-bold" data-id="element-1103">
              {shipment.items} Units
            </span>
          </div>
          <div className="flex -space-x-2" data-id="element-1104">
            <div className="w-6 h-6 rounded-full border-2 border-white bg-slate-200 flex items-center justify-center text-[10px] font-bold text-slate-500" data-id="element-1105">
              JD
            </div>
          </div>
        </div>
      </div>
    </div>;
}
function Plus({
  size
}: {
  size: number;
}) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1106">
      <line x1="12" y1="5" x2="12" y2="19" data-id="element-1107"></line>
      <line x1="5" y1="12" x2="19" y2="12" data-id="element-1108"></line>
    </svg>;
}